"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { topLoader } from "@/lib/topLoader";

/**
 * Indikator "loading" di tengah layar. Memberi tahu user bahwa ketukannya
 * diterima & sistem sedang memuat — penting saat jaringan lambat sehingga
 * halaman terasa "freeze". (Dulu berupa bar progres tipis di atas layar; diganti
 * jadi spinner di tengah agar lebih jelas terlihat user sedang menunggu.)
 *
 * Dirakit dari 3 bagian:
 *  - `LoadingOverlay`    : spinner di tengah layar, berlangganan ke `topLoader`.
 *  - `NavigationStarter` : nyalakan loader saat klik link / `history.pushState`.
 *  - `RouteChangeWatcher`: matikan loader saat pathname/search berubah (navigasi tiba).
 */

const FADE_MS = 220; // durasi fade-out sebelum spinner dilepas dari DOM

function LoadingOverlay() {
  // `mounted` menjaga elemen tetap ada selama animasi fade-out; `visible`
  // mengontrol opacity. Spinner tak butuh nilai progres — cukup aktif/tidak.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const clearHideTimer = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    };

    const unsubscribe = topLoader.subscribe((active) => {
      if (active) {
        clearHideTimer();
        setMounted(true);
        // Nyalakan opacity di frame berikutnya agar transisi fade-in berjalan.
        requestAnimationFrame(() => setVisible(true));
      } else {
        setVisible(false);
        clearHideTimer();
        hideTimer = setTimeout(() => setMounted(false), FADE_MS);
      }
    });

    return () => {
      unsubscribe();
      clearHideTimer();
    };
  }, []);

  if (!mounted) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="top-loader"
      className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/90 px-7 py-6 shadow-xl ring-1 ring-black/5 backdrop-blur-sm">
        <span
          data-testid="top-loader-bar"
          className="h-9 w-9 animate-spin rounded-full border-[3px] border-shopeeSoft border-t-shopee"
        />
        <span className="text-sm font-medium text-gray-600">Memuat…</span>
      </div>
    </div>
  );
}

/**
 * Nyalakan bar saat navigasi DIMULAI:
 *  - klik elemen `<a>`/`<Link>` same-origin (sinyal paling awal, saat ketukan).
 *  - `history.pushState` (menangkap `router.push` programatik).
 */
function NavigationStarter() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return; // hanya klik kiri
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as Element | null;
      const anchor = target?.closest?.("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;
      const targetAttr = anchor.getAttribute("target");
      if (targetAttr && targetAttr !== "_self") return; // tab/window baru
      if (anchor.hasAttribute("download")) return;

      let dest: URL;
      try {
        dest = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (dest.origin !== window.location.origin) return; // link eksternal

      const here = window.location;
      // URL sama (cuma hash / tujuan identik) → bukan navigasi, jangan nyalakan.
      if (dest.pathname === here.pathname && dest.search === here.search) return;

      topLoader.startNavigation();
    }

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  useEffect(() => {
    const w = window as typeof window & { __galeaLoaderPatched?: boolean };
    if (w.__galeaLoaderPatched) return;
    w.__galeaLoaderPatched = true;

    const original = history.pushState.bind(history);
    history.pushState = function patched(
      ...args: Parameters<History["pushState"]>
    ): void {
      topLoader.startNavigation();
      original(...args);
    };

    return () => {
      history.pushState = original;
      w.__galeaLoaderPatched = false;
    };
  }, []);

  return null;
}

/** Matikan bar begitu rute baru benar-benar tiba (pathname/search berubah). */
function RouteChangeWatcher() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const mounted = useRef(false);

  useEffect(() => {
    // Lewati render pertama (mount) — belum ada navigasi yang menunggu.
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    topLoader.finishNavigation();
  }, [pathname, search]);

  return null;
}

export function TopLoader() {
  return (
    <>
      <LoadingOverlay />
      <NavigationStarter />
      <Suspense fallback={null}>
        <RouteChangeWatcher />
      </Suspense>
    </>
  );
}
